// @flow

import { startRequest, finishRequest } from "@store/utils";
import { transformSort } from "@transformers/sort";
import { cloneObject } from "@utils/object";
import type { ManagementState } from "./types";
import initialState from "./state";

import {
  GetSystemUsers,
  GetSystemUsersSuccess,
  GetSystemUsersError,
  SetSystemUsersSort,
  UpdateSystemUser,
  UpdateSystemUserSuccess,
  UpdateSystemUserError,
  SetSystemUserStatus,
  SetSystemUserStatusSuccess,
  SetSystemUserStatusError,
  OpenAddUserModal,
  CloseAddUserModal,
  OpenEditUserModal,
  CloseEditUserModal,
  SearchEmployees,
  SearchEmployeesSuccess,
  SearchEmployeesError,
  ExportSystemUsers,
  ExportSystemUsersSuccess,
  ExportSystemUsersError,
  GetAdvisor,
  GetAdvisorSuccess,
  GetAdvisorError,
  GetAdvisors,
  GetAdvisorsSuccess,
  GetAdvisorsError,
  GetAdvisorActivity,
  GetAdvisorActivitySuccess,
  GetAdvisorActivityError,
  ExportAdvisorActivityError,
  GetCallbackQueueDashboardHeadlines,
  GetCallbackQueueDashboardHeadlinesSuccess,
  GetCallbackQueueDashboardHeadlinesError,
  GetCallbackQueueDashboardClientsSummary,
  GetCallbackQueueDashboardClientsSummarySuccess,
  GetCallbackQueueDashboardClientsSummaryError,
  GetCallbackQueueDashboardAdvisorsSummary,
  GetCallbackQueueDashboardAdvisorsSummarySuccess,
  SetAdvisorsSort,
  GetMedicalTriageScripts,
  GetMedicalTriageScriptsSuccess,
  GetMedicalTriageScriptsError,
  GetCallScripts,
  GetCallScriptsSuccess,
  GetCallScriptsError,
  ToggleEditCallScriptModal,
  ToggleEditMedicalTriageScriptModal,
  GetMedicalTriageSymptoms,
  GetMedicalTriageSymptomsSuccess,
  GetMedicalTriageSymptomsError,
  ToggleEditMedicalTriageSymptomModal,
  UpdateMedicalTriageSymptom,
  UpdateMedicalTriageSymptomSuccess,
  UpdateMedicalTriageSymptomError,
  UpdateCallScript,
  UpdateCallScriptSuccess,
  UpdateCallScriptError,
  UpdateMedicalTriageScriptError,
  GetManagementCallbackQueues,
  GetManagementCallbackQueuesSuccess,
  GetManagementCallbackQueuesError,
  GetActiveManagementCallbacks,
  GetActiveManagementCallbacksSuccess,
  GetActiveManagementCallbacksError,
  GetCompletedManagementCallbacks,
  GetCompletedManagementCallbacksSuccess,
  GetCompletedManagementCallbacksError,
  ToggleRemoveCallbackModal,
  ToggleRescheduleCallbackModal,
  ToggleCreateCallbackModal,
  CallbacksEmployeeSearch,
  CallbacksEmployeeSearchError,
  CallbacksEmployeeSearchSuccess,
  CallbacksEmployeeSearchResetResults,
  CreateCallback,
  CreateCallbackSuccess,
  CreateCallbackError,
  GetCallbackScheduledWindows,
  GetCallbackScheduledWindowsSuccess,
  GetCallbackScheduledWindowsError,
  ToggleEditCallbackQueueModal,
  ChangeEditCallbackQueueModal,
  BulkChangeEditCallbackQueueModal,
  UpdateCallbackScheduledWindows,
  UpdateCallbackScheduledWindowsSuccess,
  UpdateCallbackScheduledWindowsError,
  GetMessagesLoginText,
  GetMessagesLoginTextSuccess,
  GetMessagesLoginTextError,
  UpdateMessagesLoginText,
  GetMessagesReleaseVersion,
  GetMessagesReleaseVersionSuccess,
  GetMessagesReleaseVersionError,
  ToggleLoginMessageModal,
  ToggleReleaseNoteModal,
  UpdateReleaseNote,
  CallbacksEmployeeExtendedSearchSuccess,
  CallbacksEmployeeExtendedSearch,
  ToggleLogNewIncidentModal,
  GetClinicalIncidentsRegister,
  GetClinicalIncidentsRegisterSuccess,
  GetIncidentRateReportGridData,
  GetIncidentRateReportGridDataSuccess,
  GetIncidentRateReportGridDataError,
  GetIncidentTypes,
  GetIncidentTypesSuccess,
  GetIncidentTypesError,
  GetIncidentCauses,
  GetIncidentCausesSuccess,
  GetIncidentCausesError,
  GetCallHandlersSuccess,
  GetClinicalIncidentsRegisterError,
  GetClinicalIncidentsRegisterExport,
  GetClinicalIncidentsRegisterExportSuccess,
  GetClinicalIncidentsRegisterExportError,
  ToggleViewEditIncidentModal,
  ExportCallbackQueueDashboardClientsSummary,
  ExportCallbackQueueDashboardClientsSummarySuccess,
  ExportCallbackQueueDashboardClientsSummaryError,
  ExportCallbackQueueDashboardAdvisorsSummary,
  ExportCallbackQueueDashboardAdvisorsSummarySuccess,
  ExportCallbackQueueDashboardAdvisorsSummaryError,
  GetCallbackQueueDashboardAdvisorsSummaryError,
  ExportAdvisorActivity,
  ExportAdvisorActivitySuccess,
  UpdateManagementCallback,
  UpdateManagementCallbackSuccess,
  UpdateManagementCallbackError,
  ExportIncidentRateReport,
  ExportIncidentRateReportSuccess,
  ExportIncidentRateReportError,
  GetIncidentRateReportHeadlines,
  GetIncidentRateReportHeadlinesError,
  GetIncidentRateReportHeadlinesSuccess,
  GetServiceDeskSettings,
  GetServiceDeskSettingsSuccess,
  GetServiceDeskSettingsError,
  UpdateServiceDeskSettings,
  UpdateServiceDeskSettingsSuccess,
  UpdateServiceDeskSettingsError,
  GetHealthNotices,
  GetHealthNoticesSuccess,
  GetHealthNoticesError,
  UpdateHealthNotice,
  UpdateHealthNoticeError,
  UpdateHealthNoticeSuccess,
  AddHealthNotice,
  AddHealthNoticeSuccess,
  AddHealthNoticeError,
  GetHealthNotice,
  GetHealthNoticeSuccess,
  GetHealthNoticeError,
  PublishHealthNotice,
  PublishHealthNoticeSuccess,
  PublishHealthNoticeError,
  DeleteHealthNotice,
  DeleteHealthNoticeSuccess,
  DeleteHealthNoticeError,
  CancelHealthNotice,
  CancelHealthNoticeSuccess,
  CancelHealthNoticeError,
  GetImages,
  GetImagesSuccess,
  GetImagesError,
  UploadImage,
  UploadImageSuccess,
  UploadImageError,
  GetAllergies,
  GetAllergiesSuccess,
  GetAllergiesError,
  AddAllergy,
  AddAllergySuccess,
  AddAllergyError,
  UpdateAllergy,
  UpdateAllergySuccess,
  UpdateAllergyError,
  SetAllergyModal,
  GetConditions,
  GetConditionsSuccess,
  GetConditionsError,
  AddCondition,
  AddConditionSuccess,
  AddConditionError,
  UpdateCondition,
  UpdateConditionSuccess,
  UpdateConditionError,
  SetConditionModal,
  GetMedications,
  GetMedicationsSuccess,
  GetMedicationsError,
  GetMedicationTypes,
  GetMedicationTypesSuccess,
  GetMedicationTypesError,
  AddMedication,
  AddMedicationSuccess,
  AddMedicationError,
  UpdateMedication,
  UpdateMedicationSuccess,
  UpdateMedicationError,
  SetMedicationModal,
  GetVaccinations,
  GetVaccinationsSuccess,
  GetVaccinationsError,
  AddVaccination,
  AddVaccinationSuccess,
  AddVaccinationError,
  UpdateVaccination,
  UpdateVaccinationSuccess,
  UpdateVaccinationError,
  SetVaccinationModal,
  GetProviders,
  GetProvidersSuccess,
  GetProvidersError,
  AddProvider,
  AddProviderSuccess,
  AddProviderError,
  UpdateProvider,
  UpdateProviderSuccess,
  UpdateProviderError,
  SetProviderModal,
  GetProviderLogo,
  GetProviderLogoSuccess,
  GetProviderLogoError,
  GetServiceTypes,
  GetServiceTypesSuccess,
  GetServiceTypesError,
  AddServiceType,
  AddServiceTypeSuccess,
  AddServiceTypeError,
  UpdateServiceType,
  UpdateServiceTypeSuccess,
  UpdateServiceTypeError,
  SetServiceTypeModal,
  GetWellbeingCategories,
  GetWellbeingCategoriesSuccess,
  GetWellbeingCategoriesError,
  GetParentAbsenceReasons,
  GetParentAbsenceReasonsSuccess,
  GetParentAbsenceReasonsError,
  GetCarePlanActivities,
  GetCarePlanActivitiesSuccess,
  GetCarePlanActivitiesError,
  GetCarePlanActivityTypes,
  GetCarePlanActivityTypesSuccess,
  GetCarePlanActivityTypesError,
  GetCarePlanActivityPeriods,
  GetCarePlanActivityPeriodsSuccess,
  GetCarePlanActivityPeriodsError,
  GetCarePlanActivityParents,
  GetCarePlanActivityParentsSuccess,
  GetCarePlanActivityParentsError,
  GetCarePlanActivityCounters,
  GetCarePlanActivityCountersSuccess,
  GetCarePlanActivityCountersError,
  AddCarePlanActivity,
  AddCarePlanActivitySuccess,
  AddCarePlanActivityError,
  UpdateCarePlanActivity,
  UpdateCarePlanActivitySuccess,
  UpdateCarePlanActivityError,
  SetCarePlanActivityModal,
  GetSupportPlanActivities,
  GetSupportPlanActivitiesSuccess,
  GetSupportPlanActivitiesError,
  GetSupportPlanActivityCounters,
  GetSupportPlanActivityCountersSuccess,
  GetSupportPlanActivityCountersError,
  AddSupportPlanActivity,
  AddSupportPlanActivitySuccess,
  AddSupportPlanActivityError,
  UpdateSupportPlanActivity,
  UpdateSupportPlanActivitySuccess,
  UpdateSupportPlanActivityError,
  SetSupportPlanActivityModal,
  GetSupportPlanJobDutyConfigs,
  GetSupportPlanJobDutyConfigsSuccess,
  GetSupportPlanJobDutyConfigsError,
  GetTriageQuestions,
  GetTriageQuestionsSuccess,
  GetTriageQuestionsError,
  GetTriageQuestionSymptoms,
  GetTriageQuestionSymptomsSuccess,
  GetTriageQuestionSymptomsError,
  AddTriageQuestion,
  AddTriageQuestionSuccess,
  AddTriageQuestionError,
  UpdateTriageQuestion,
  UpdateTriageQuestionSuccess,
  UpdateTriageQuestionError,
  SetTriageQuestionModal,
  GetTriageAdvices,
  GetTriageAdvicesSuccess,
  GetTriageAdvicesError,
  GetTriageAdviceSymptoms,
  GetTriageAdviceSymptomsSuccess,
  GetTriageAdviceSymptomsError,
  AddTriageAdvice,
  AddTriageAdviceSuccess,
  AddTriageAdviceError,
  UpdateTriageAdvice,
  UpdateTriageAdviceSuccess,
  UpdateTriageAdviceError,
  SetTriageAdviceModal,
} from "@modules/management/constants/actions";

export const setLoading = (state: ManagementState) => () => {
  startRequest(state);
  return state;
};

export const setLoaded = (state: ManagementState) => () => {
  finishRequest(state);
  return state;
};

export const onError = (state: ManagementState) => ({ payload }: Object) => {
  state.error = payload;
  finishRequest(state);
  return state;
};

export const onGetSystemUsersSuccess = (state: ManagementState) => ({ payload }: Object) => {
  state.error = null;
  state.users = payload;
  finishRequest(state);
  return state;
};

export const onGetAllergiesSuccess = (state: ManagementState) => ({ payload }: Object) => {
  state.error = null;
  state.allergies = payload;
  finishRequest(state);
  return state;
};

export const onSetAllergyModal = (state: ManagementState) => ({ payload }: Object) => {
  state.modals.allergy.visible = payload.visible;
  state.modals.allergy.data = payload.data;
  return state;
};

export const onGetConditionsSuccess = (state: ManagementState) => ({ payload }: Object) => {
  state.error = null;
  state.conditions = payload;
  finishRequest(state);
  return state;
};

export const onSetConditionModal = (state: ManagementState) => ({ payload }: Object) => {
  state.modals.condition.visible = payload.visible;
  state.modals.condition.data = payload.data;
  return state;
};

export const onGetMedicationsSuccess = (state: ManagementState) => ({ payload }: Object) => {
  state.error = null;
  state.medications = payload;
  finishRequest(state);
  return state;
};

export const onGetMedicationTypesSuccess = (state: ManagementState) => ({ payload }: Object) => {
  state.error = null;
  state.medicationTypes = payload;
  finishRequest(state);
  return state;
};

export const onSetMedicationModal = (state: ManagementState) => ({ payload }: Object) => {
  state.modals.medication.visible = payload.visible;
  state.modals.medication.data = payload.data;
  return state;
};

export const onGetVaccinationsSuccess = (state: ManagementState) => ({ payload }: Object) => {
  state.error = null;
  state.vaccinations = payload;
  finishRequest(state);
  return state;
};

export const onSetVaccinationModal = (state: ManagementState) => ({ payload }: Object) => {
  state.modals.vaccination.visible = payload.visible;
  state.modals.vaccination.data = payload.data;
  return state;
};

export const onGetProvidersSuccess = (state: ManagementState) => ({ payload }: Object) => {
  state.error = null;
  state.providers = payload;
  finishRequest(state);
  return state;
};

export const onSetProviderModal = (state: ManagementState) => ({ payload }: Object) => {
  state.modals.provider.visible = payload.visible;
  state.modals.provider.data = payload.data;
  state.modals.provider.logoUrl = null;
  return state;
};

export const onGetProviderLogoSuccess = (state: ManagementState) => ({ payload }: Object) => {
  state.error = null;
  state.modals.provider.logoUrl = payload;
  finishRequest(state);
  return state;
};

export const onGetServiceTypesSuccess = (state: ManagementState) => ({ payload }: Object) => {
  state.error = null;
  state.serviceTypes = payload;
  finishRequest(state);
  return state;
};

export const onSetServiceTypeModal = (state: ManagementState) => ({ payload }: Object) => {
  state.modals.serviceType.visible = payload.visible;
  state.modals.serviceType.data = payload.data;
  return state;
};

export const onGetWellbeingCategoriesSuccess = (state: ManagementState) => ({
  payload,
}: Object) => {
  state.error = null;
  state.wellbeingCategories = payload;
  finishRequest(state);
  return state;
};

export const onGetParentAbsenceReasonsSuccess = (state: ManagementState) => ({
  payload,
}: Object) => {
  state.error = null;
  state.parentAbsenceReasons = payload;
  finishRequest(state);
  return state;
};

export const onGetCarePlanActivitiesSuccess = (state: ManagementState) => ({ payload }: Object) => {
  state.error = null;
  state.carePlanActivities = payload;
  finishRequest(state);
  return state;
};

export const onGetCarePlanActivityTypesSuccess = (state: ManagementState) => ({
  payload,
}: Object) => {
  state.error = null;
  state.carePlanActivityTypes = payload;
  finishRequest(state);
  return state;
};

export const onGetCarePlanActivityPeriodsSuccess = (state: ManagementState) => ({
  payload,
}: Object) => {
  state.error = null;
  state.carePlanActivityPeriods = payload;
  finishRequest(state);
  return state;
};

export const onGetCarePlanActivityParentsSuccess = (state: ManagementState) => ({
  payload,
}: Object) => {
  state.error = null;
  state.carePlanActivityParents = payload;
  finishRequest(state);
  return state;
};

export const onGetCarePlanActivityCountersSuccess = (state: ManagementState) => ({
  payload,
}: Object) => {
  state.error = null;
  state.carePlanActivityCounters = payload;
  finishRequest(state);
  return state;
};

export const onSetCarePlanActivityModal = (state: ManagementState) => ({ payload }: Object) => {
  state.modals.carePlanActivity.visible = payload.visible;
  state.modals.carePlanActivity.data = payload.data;
  return state;
};

export const onGetSupportPlanActivitiesSuccess = (state: ManagementState) => ({
  payload,
}: Object) => {
  state.error = null;
  state.supportPlanActivities = payload;
  finishRequest(state);
  return state;
};

export const onGetSupportPlanActivityCountersSuccess = (state: ManagementState) => ({
  payload,
}: Object) => {
  state.error = null;
  state.supportPlanActivityCounters = payload;
  finishRequest(state);
  return state;
};

export const onSetSupportPlanActivityModal = (state: ManagementState) => ({ payload }: Object) => {
  state.modals.supportPlanActivity.visible = payload.visible;
  state.modals.supportPlanActivity.data = payload.data;
  return state;
};

export const onGetSupportPlanJobDutyConfigsSuccess = (state: ManagementState) => ({
  payload,
}: Object) => {
  state.error = null;
  state.supportPlanJobDutyConfigs = payload;
  finishRequest(state);
  return state;
};

export const onGetTriageQuestionsSuccess = (state: ManagementState) => ({ payload }: Object) => {
  state.error = null;
  state.triageQuestions = payload;
  finishRequest(state);
  return state;
};

export const onGetTriageQuestionSymptomsSuccess = (state: ManagementState) => ({
  payload,
}: Object) => {
  state.error = null;
  state.triageQuestionSymptoms = payload;
  finishRequest(state);
  return state;
};

export const onSetTriageQuestionModal = (state: ManagementState) => ({ payload }: Object) => {
  state.modals.triageQuestion.visible = payload.visible;
  state.modals.triageQuestion.data = payload.data;
  return state;
};

export const onGetTriageAdvicesSuccess = (state: ManagementState) => ({ payload }: Object) => {
  state.error = null;
  state.triageAdvices = payload;
  finishRequest(state);
  return state;
};

export const onGetTriageAdviceSymptomsSuccess = (state: ManagementState) => ({
  payload,
}: Object) => {
  state.error = null;
  state.triageAdviceSymptoms = payload;
  finishRequest(state);
  return state;
};

export const onSetTriageAdviceModal = (state: ManagementState) => ({ payload }: Object) => {
  state.modals.triageAdvice.visible = payload.visible;
  state.modals.triageAdvice.data = payload.data;
  return state;
};

export const onGetManagementCallbackQueuesSuccess = (state: ManagementState) => ({
  payload,
}: Object) => {
  state.error = null;
  finishRequest(state);
  state.callbackQueues = payload;
  return state;
};

export const onGetActiveManagementCallbacksSuccess = (state: ManagementState) => ({
  payload,
}: Object) => {
  state.error = null;
  state.activeCallbacks = payload;
  finishRequest(state);
  return state;
};

export const onGetCompletedManagementCallbacksSuccess = (state: ManagementState) => ({
  payload,
}: Object) => {
  state.error = null;
  state.completedCallbacks = payload;
  finishRequest(state);
  return state;
};

export const onToggleRemoveCallbackModal = (state: ManagementState) => ({ payload }: Object) => {
  state.modals.removeCallback.visible = payload;
  return state;
};

export const onToggleRescheduleCallbackModal = (state: ManagementState) => ({
  payload,
}: Object) => {
  state.modals.rescheduleCallback.visible = payload;
  return state;
};

export const onToggleCreateCallbackModal = (state: ManagementState) => ({ payload }: Object) => {
  state.modals.createCallback.visible = payload;
  return state;
};

export const onCallbacksEmployeeSearchSuccess = (state: ManagementState) => ({
  payload,
}: Object) => {
  state.employees = payload;
  state.error = null;
  finishRequest(state);
  return state;
};
export const onCallbacksEmployeeSearchResetResults = (state: ManagementState) => () => {
  state.employees = initialState.employees;
  return state;
};

export const onCallbacksEmployeeExtendedSearchSuccess = (state: ManagementState) => ({
  payload,
}: Object) => {
  state.employees = payload;
  state.error = null;
  finishRequest(state);
  return state;
};

export const onSearchEmployeesSuccess = (state: ManagementState) => ({ payload }: Object) => {
  state.error = null;
  state.employees = payload;
  finishRequest(state);
  return state;
};

export const onSetSystemUsersSort = (state: ManagementState) => ({ payload }: Object) => {
  startRequest(state);
  ((state.users.pagination || {}).sort || {}).orders = transformSort(payload);
  return state;
};

export const onUpdateSystemUserSuccess = (state: ManagementState) => ({ payload }: Object) => {
  state.error = null;
  finishRequest(state);
  return state;
};

export const onSetSystemUserStatusSuccess = (state: ManagementState) => ({ payload }: Object) => {
  state.error = null;
  finishRequest(state);
  return state;
};

export const onOpenAddUserModal = (state: ManagementState) => ({ payload }: Object) => {
  state.modals.add.visible = true;
  state.modals.add.user = payload;
  return state;
};

export const onCloseAddUserModal = (state: ManagementState) => (action: any) => {
  state.employees = initialState.employees;
  state.modals.add = initialState.modals.add;
  return state;
};

export const onOpenEditUserModal = (state: ManagementState) => ({ payload }: Object) => {
  state.modals.edit.visible = true;
  state.modals.edit.user = payload;
  return state;
};

export const onCloseEditUserModal = (state: ManagementState) => () => {
  state.modals.edit.visible = false;
  state.modals.edit.user = null;
  return state;
};

export const onExportSystemUsersSuccess = (state: ManagementState) => () => {
  state.error = null;
  finishRequest(state);
  return state;
};

export const onGetAdvisorSuccess = (state: ManagementState) => ({ payload }: Object) => {
  state.error = null;
  finishRequest(state);
  state.advisors.current = payload;
  return state;
};

export const onGetAdvisorError = (state: ManagementState) => ({ payload }: Object) => {
  state.error = payload;
  finishRequest(state);
  state.advisors.current = null;
  return state;
};

export const onGetAdvisorsSuccess = (state: ManagementState) => ({ payload }: Object) => {
  state.error = null;
  finishRequest(state);
  state.advisors = payload;
  return state;
};

export const onSetAdvisorsSort = (state: ManagementState) => ({ payload }: { payload: Object }) => {
  startRequest(state);
  ((state.advisors.pagination || {}).sort || {}).orders = transformSort(payload);
  return state;
};

export const onGetAdvisorActivitySuccess = (state: ManagementState) => ({ payload }: Object) => {
  state.error = null;
  finishRequest(state);
  state.advisorActivity = payload;
  return state;
};

export const onGetCallbackQueueDashboardHeadlinesSuccess = (state: ManagementState) => ({
  payload,
}: Object) => {
  finishRequest(state);
  state.dashboard.headlines = payload;
  return state;
};

export const onGetCallbackQueueDashboardClientsSummarySuccess = (state: ManagementState) => ({
  payload,
}: Object) => {
  finishRequest(state);
  state.dashboard.clientsSummary = payload;
  return state;
};

export const onGetCallbackQueueDashboardAdvisorsSummarySuccess = (state: ManagementState) => ({
  payload,
}: Object) => {
  finishRequest(state);
  state.dashboard.advisorsSummary = payload;
  return state;
};

export const onGetMedicalTriageScriptsSuccess = (state: ManagementState) => ({
  payload,
}: Object) => {
  state.error = null;
  finishRequest(state);
  state.medicalTriageScripts.content = payload;
  return state;
};

export const onGetMedicalTriageScriptsError = (state: ManagementState) => ({ payload }: Object) => {
  state.medicalTriageScripts.content = [];
  finishRequest(state);
  state.error = payload;
  return state;
};

export const onGetCallScriptsSuccess = (state: ManagementState) => ({ payload }: Object) => {
  state.error = null;
  finishRequest(state);
  state.callScripts.content = payload;
  return state;
};

export const onGetCallScriptsError = (state: ManagementState) => ({ payload }: Object) => {
  state.callScripts.content = [];
  state.error = payload;
  finishRequest(state);
  return state;
};

export const onGetMedicalTriageSymptomsSuccess = (state: ManagementState) => ({
  payload,
}: Object) => {
  state.error = null;
  finishRequest(state);
  state.medicalTriageSymptoms.content = payload;
  return state;
};

export const onGetMedicalTriageSymptomsError = (state: ManagementState) => ({
  payload,
}: Object) => {
  state.medicalTriageSymptoms.content = [];
  state.error = payload;
  finishRequest(state);
  return state;
};

export const onGetCallbackScheduledWindowsSuccess = (state: ManagementState) => ({
  payload,
}: Object) => {
  finishRequest(state);
  state.scheduledWindows.content = payload;
  state.error = null;
  return state;
};

export const onGetCallbackScheduledWindowsError = (state: ManagementState) => ({
  payload,
}: Object) => {
  state.scheduledWindows.content = [];
  finishRequest(state);
  state.error = payload;
  return state;
};

export const onToggleEditCallbackQueueModal = (state: ManagementState) => ({ payload }: Object) => {
  state.modals.editCallbackQueue.visible = !state.modals.editCallbackQueue.visible;
  state.modals.editCallbackQueue.scheduledWindows = state.scheduledWindows.content;
  return state;
};

export const onChangeEditCallbackQueueModal = (state: ManagementState) => ({ payload }: Object) => {
  state.modals.editCallbackQueue.scheduledWindows = state.modals.editCallbackQueue.scheduledWindows.map(
    item => (item.start === payload.start ? payload : item),
  );
  return state;
};

export const onBulkChangeEditCallbackQueueModal = (state: ManagementState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    modals: {
      ...state.modals,
      editCallbackQueue: {
        ...state.modals.editCallbackQueue,
        scheduledWindows: state.modals.editCallbackQueue.scheduledWindows.map(window => {
          return payload.days.includes(window.start.slice(0, 10))
            ? { ...window, rate: payload.rate }
            : window;
        }),
      },
    },
  };
};

export const onUpdateCallbackScheduledWindowsSuccess = (state: ManagementState) => () => {
  state.modals.editCallbackQueue.visible = false;
  finishRequest(state);
  return state;
};

export const onToggleEditCallScriptModal = (state: ManagementState) => ({ payload }: Object) => {
  finishRequest(state);
  state.error = null;
  const editCallScript = state.modals.editCallScript;
  editCallScript.visible = payload.visible;
  editCallScript.id = payload.id;
  editCallScript.formData.instructionText = payload.instructionText;
  editCallScript.displayName = payload.displayName;
  editCallScript.formData.scriptText = payload.scriptText;
  return state;
};

export const onToggleEditMedicalTriageScriptModal = (state: ManagementState) => ({
  payload,
}: Object) => {
  finishRequest(state);
  state.error = null;
  state.modals.editMedicalTriageScript.visible = payload.visible;
  state.modals.editMedicalTriageScript.id = payload.id;
  state.modals.editMedicalTriageScript.displayName = payload.displayName;
  state.modals.editMedicalTriageScript.formData.scriptText = payload.scriptText;
  return state;
};

export const onToggleEditMedicalTriageSymptomModal = (state: ManagementState) => ({
  payload,
}: Object) => {
  state.error = null;

  state.modals.editMedicalTriageSymptom.visible = payload.visible;
  state.modals.editMedicalTriageSymptom.id = payload.id;
  state.modals.editMedicalTriageSymptom.name = payload.name;
  state.modals.editMedicalTriageSymptom.formData.guidanceNotes = payload.guidanceNotes;
  finishRequest(state);
  return state;
};

const onGetMessagesLoginTextSuccess = (state: ManagementState) => ({ payload }: Object) => {
  finishRequest(state);
  state.error = null;
  state.login = payload;
  state.modals.editLogin.visible = false;
  return state;
};

const onToggleLoginMessageModal = (state: ManagementState) => () => ({
  ...state,
  modals: {
    ...state.modals,
    editLogin: {
      visible: !state.modals.editLogin.visible,
    },
  },
});

const onGetMessagesReleaseVersionSuccess = (state: ManagementState) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
  releaseNote: payload,
  modals: {
    ...state.modals,
    releaseNote: {
      visible: false,
    },
  },
});

const onToggleReleaseNoteModal = (state: ManagementState) => () => ({
  ...state,
  modals: {
    ...state.modals,
    releaseNote: {
      visible: !state.modals.releaseNote.visible,
    },
  },
});

const onToggleLogNewIncidentModal = (state: ManagementState) => () => ({
  ...state,
  modals: {
    ...state.modals,
    logNewIncident: {
      visible: !state.modals.logNewIncident.visible,
    },
  },
});

const onToggleViewEditIncidentModal = (state: ManagementState) => ({ payload }: Object) => {
  return {
    ...state,
    modals: {
      ...state.modals,
      viewEditIncident: {
        visible: !state.modals.viewEditIncident.visible,
        data: payload.data,
        isEditable: payload.isEditable,
      },
    },
  };
};

export const onGetMessagesLoginTextError = (state: ManagementState) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  error: payload,
});

export const onGetMessagesReleaseVersionError = (state: ManagementState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  error: payload,
});

export const onGetClinicalIncidentsRegisterSuccess = (state: ManagementState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  incidentRegister: payload,
});

export const onGetIncidentRateReportGridDataSuccess = (state: ManagementState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  incidentRatesReport: {
    ...state.incidentRatesReport,
    ...payload,
  },
});

export const onGetIncidentRateReportGridDataError = (state: ManagementState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  error: payload,
});

export const onGetIncidentTypesSuccess = (state: ManagementState) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  incidentTypes: payload,
});

export const onGetIncidentCausesSuccess = (state: ManagementState) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  incidentCauses: payload,
});

export const onGetCallHandlersSuccess = (state: ManagementState) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  callHandlers: payload,
});

export const onGetClinicalIncidentsRegisterError = (state: ManagementState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  error: payload,
});

const onGetServiceDeskSettingsSuccess = (state: ManagementState) => ({ payload }) => ({
  ...state,
  ...finishRequest(state),
  serviceDesk: payload,
});

const onGetHealthNoticesSuccess = (state: ManagementState) => ({ payload }) => ({
  ...state,
  ...finishRequest(state),
  healthNotices: {
    current: null,
    list: payload,
  },
});

const onGetHealthNotice = (state: ManagementState) => ({ payload }) => ({
  ...state,
  ...startRequest(state),
  healthNotices: {
    ...state.healthNotices,
    current: null,
  },
});

const onGetHealthNoticeSuccess = (state: ManagementState) => ({ payload }) => ({
  ...state,
  ...finishRequest(state),
  healthNotices: {
    ...state.healthNotices,
    current: payload,
  },
});

const onAddHealthNotice = (state: ManagementState) => ({ payload }) => ({
  ...state,
  ...startRequest(state),
  healthNotices: {
    ...state.healthNotices,
    current: payload.data,
  },
});

const onGetImagesSuccess = (state: ManagementState) => ({ payload }) => ({
  ...state,
  ...finishRequest(state),
  images: {
    list: payload,
  },
});

export default (state: ManagementState = initialState, action: *): ManagementState => {
  let act = {
    [GetSystemUsers]: setLoading,
    [GetSystemUsersSuccess]: onGetSystemUsersSuccess,
    [GetSystemUsersError]: onError,
    [GetManagementCallbackQueues]: setLoading,
    [GetManagementCallbackQueuesSuccess]: onGetManagementCallbackQueuesSuccess,
    [GetManagementCallbackQueuesError]: onError,
    [GetActiveManagementCallbacks]: setLoading,
    [GetActiveManagementCallbacksSuccess]: onGetActiveManagementCallbacksSuccess,
    [GetActiveManagementCallbacksError]: onError,
    [GetCompletedManagementCallbacks]: setLoading,
    [GetCompletedManagementCallbacksSuccess]: onGetCompletedManagementCallbacksSuccess,
    [ToggleRemoveCallbackModal]: onToggleRemoveCallbackModal,
    [ToggleRescheduleCallbackModal]: onToggleRescheduleCallbackModal,
    [ToggleCreateCallbackModal]: onToggleCreateCallbackModal,
    [GetCompletedManagementCallbacksError]: onError,
    [SetSystemUsersSort]: onSetSystemUsersSort,
    [UpdateSystemUser]: setLoading,
    [UpdateSystemUserSuccess]: onUpdateSystemUserSuccess,
    [UpdateSystemUserError]: onError,
    [SetSystemUserStatus]: setLoading,
    [SetSystemUserStatusSuccess]: onSetSystemUserStatusSuccess,
    [SetSystemUserStatusError]: onError,
    [OpenAddUserModal]: onOpenAddUserModal,
    [CloseAddUserModal]: onCloseAddUserModal,
    [OpenEditUserModal]: onOpenEditUserModal,
    [CloseEditUserModal]: onCloseEditUserModal,
    [SearchEmployees]: setLoading,
    [SearchEmployeesSuccess]: onSearchEmployeesSuccess,
    [SearchEmployeesError]: onError,
    [CreateCallback]: setLoading,
    [CreateCallbackSuccess]: setLoaded,
    [CreateCallbackError]: setLoaded,
    [CallbacksEmployeeSearch]: setLoading,
    [CallbacksEmployeeSearchSuccess]: onCallbacksEmployeeSearchSuccess,
    [CallbacksEmployeeSearchResetResults]: onCallbacksEmployeeSearchResetResults,
    [CallbacksEmployeeSearchError]: onError,
    [CallbacksEmployeeExtendedSearch]: setLoading,
    [CallbacksEmployeeExtendedSearchSuccess]: onCallbacksEmployeeExtendedSearchSuccess,
    [ExportSystemUsers]: setLoading,
    [ExportSystemUsersSuccess]: onExportSystemUsersSuccess,
    [ExportSystemUsersError]: onError,
    [GetAdvisorActivity]: setLoading,
    [GetAdvisorActivitySuccess]: onGetAdvisorActivitySuccess,
    [GetAdvisorActivityError]: onError,
    [ExportAdvisorActivity]: setLoading,
    [ExportAdvisorActivitySuccess]: setLoaded,
    [ExportAdvisorActivityError]: onError,
    [UpdateManagementCallback]: setLoading,
    [UpdateManagementCallbackSuccess]: setLoaded,
    [UpdateManagementCallbackError]: onError,

    // Headlines
    [GetCallbackQueueDashboardHeadlines]: setLoading,
    [GetCallbackQueueDashboardHeadlinesSuccess]: onGetCallbackQueueDashboardHeadlinesSuccess,
    [GetCallbackQueueDashboardHeadlinesError]: onError,
    // Clients summary
    [GetCallbackQueueDashboardClientsSummary]: setLoading,
    [GetCallbackQueueDashboardClientsSummarySuccess]: onGetCallbackQueueDashboardClientsSummarySuccess,
    [GetCallbackQueueDashboardClientsSummaryError]: onError,
    [ExportCallbackQueueDashboardClientsSummary]: setLoading,
    [ExportCallbackQueueDashboardClientsSummarySuccess]: setLoaded,
    [ExportCallbackQueueDashboardClientsSummaryError]: onError,

    // Advisors
    [GetCallbackQueueDashboardAdvisorsSummary]: setLoading,
    [GetCallbackQueueDashboardAdvisorsSummarySuccess]: onGetCallbackQueueDashboardAdvisorsSummarySuccess,
    [GetCallbackQueueDashboardAdvisorsSummaryError]: onError,
    [SetAdvisorsSort]: onSetAdvisorsSort,
    [ExportCallbackQueueDashboardAdvisorsSummary]: setLoading,
    [ExportCallbackQueueDashboardAdvisorsSummarySuccess]: setLoaded,
    [ExportCallbackQueueDashboardAdvisorsSummaryError]: onError,
    [GetAdvisor]: setLoading,
    [GetAdvisorSuccess]: onGetAdvisorSuccess,
    [GetAdvisorError]: onGetAdvisorError,
    [GetAdvisors]: setLoading,
    [GetAdvisorsSuccess]: onGetAdvisorsSuccess,
    [GetAdvisorsError]: onError,
    // Queue Management
    [GetCallbackScheduledWindows]: setLoading,
    [GetCallbackScheduledWindowsSuccess]: onGetCallbackScheduledWindowsSuccess,
    [GetCallbackScheduledWindowsError]: onGetCallbackScheduledWindowsError,
    [ToggleEditCallbackQueueModal]: onToggleEditCallbackQueueModal,
    [ChangeEditCallbackQueueModal]: onChangeEditCallbackQueueModal,
    [BulkChangeEditCallbackQueueModal]: onBulkChangeEditCallbackQueueModal,
    [UpdateCallbackScheduledWindows]: setLoading,
    [UpdateCallbackScheduledWindowsSuccess]: onUpdateCallbackScheduledWindowsSuccess,
    [UpdateCallbackScheduledWindowsError]: onError,
    // Script Management
    [GetMedicalTriageScripts]: setLoading,
    [GetMedicalTriageScriptsSuccess]: onGetMedicalTriageScriptsSuccess,
    [GetMedicalTriageScriptsError]: onGetMedicalTriageScriptsError,
    [GetCallScripts]: setLoading,
    [GetCallScriptsSuccess]: onGetCallScriptsSuccess,
    [GetCallScriptsError]: onGetCallScriptsError,
    [ToggleEditCallScriptModal]: onToggleEditCallScriptModal,
    [ToggleEditMedicalTriageScriptModal]: onToggleEditMedicalTriageScriptModal,
    [GetMedicalTriageSymptoms]: setLoading,
    [GetMedicalTriageSymptomsSuccess]: onGetMedicalTriageSymptomsSuccess,
    [GetMedicalTriageSymptomsError]: onGetMedicalTriageSymptomsError,
    [ToggleEditMedicalTriageSymptomModal]: onToggleEditMedicalTriageSymptomModal,
    [UpdateMedicalTriageSymptom]: setLoading,
    [UpdateMedicalTriageSymptomSuccess]: setLoaded,
    [UpdateMedicalTriageSymptomError]: onError,
    [UpdateCallScript]: setLoading,
    [UpdateCallScriptSuccess]: setLoaded,
    [UpdateCallScriptError]: onError,
    [UpdateMedicalTriageScriptError]: onError,
    // System Message
    [GetMessagesLoginText]: setLoading,
    [GetMessagesLoginTextSuccess]: onGetMessagesLoginTextSuccess,
    [GetMessagesLoginTextError]: onGetMessagesLoginTextError,
    [UpdateMessagesLoginText]: setLoading,
    [ToggleLoginMessageModal]: onToggleLoginMessageModal,
    [GetMessagesReleaseVersion]: setLoading,
    [GetMessagesReleaseVersionSuccess]: onGetMessagesReleaseVersionSuccess,
    [GetMessagesReleaseVersionError]: onGetMessagesReleaseVersionError,
    [ToggleReleaseNoteModal]: onToggleReleaseNoteModal,
    [UpdateReleaseNote]: setLoading,

    // Clinical Governance
    [ToggleLogNewIncidentModal]: onToggleLogNewIncidentModal,
    [ToggleViewEditIncidentModal]: onToggleViewEditIncidentModal,

    [GetClinicalIncidentsRegister]: setLoading,
    [GetClinicalIncidentsRegisterExport]: setLoading,
    [GetClinicalIncidentsRegisterExportSuccess]: setLoaded,
    [GetClinicalIncidentsRegisterExportError]: setLoaded,
    [GetIncidentTypes]: setLoading,
    [GetIncidentTypesSuccess]: onGetIncidentTypesSuccess,
    [GetIncidentTypesError]: setLoaded,
    [GetIncidentCauses]: setLoading,
    [GetIncidentCausesSuccess]: onGetIncidentCausesSuccess,
    [GetIncidentCausesError]: setLoaded,
    [GetClinicalIncidentsRegisterSuccess]: onGetClinicalIncidentsRegisterSuccess,
    [GetIncidentRateReportGridData]: setLoading,
    [GetIncidentRateReportGridDataSuccess]: onGetIncidentRateReportGridDataSuccess,
    [GetIncidentRateReportGridDataError]: onGetIncidentRateReportGridDataError,
    [GetCallHandlersSuccess]: onGetCallHandlersSuccess,
    [GetClinicalIncidentsRegisterError]: onGetClinicalIncidentsRegisterError,
    [ExportIncidentRateReport]: setLoading,
    [ExportIncidentRateReportSuccess]: setLoaded,
    [ExportIncidentRateReportError]: setLoaded,
    [GetIncidentRateReportHeadlines]: setLoading,
    [GetIncidentRateReportHeadlinesSuccess]: onGetIncidentRateReportGridDataSuccess,
    [GetIncidentRateReportHeadlinesError]: setLoaded,
    [GetServiceDeskSettings]: setLoading,
    [GetServiceDeskSettingsSuccess]: onGetServiceDeskSettingsSuccess,
    [GetServiceDeskSettingsError]: setLoaded,
    [UpdateServiceDeskSettings]: setLoading,
    [UpdateServiceDeskSettingsSuccess]: setLoaded,
    [UpdateServiceDeskSettingsError]: setLoaded,

    // Health Notices
    [GetHealthNotices]: setLoading,
    [GetHealthNoticesSuccess]: onGetHealthNoticesSuccess,
    [GetHealthNoticesError]: setLoaded,
    [AddHealthNotice]: onAddHealthNotice,
    [AddHealthNoticeSuccess]: setLoaded,
    [AddHealthNoticeError]: setLoaded,
    [GetHealthNotice]: onGetHealthNotice,
    [GetHealthNoticeSuccess]: onGetHealthNoticeSuccess,
    [GetHealthNoticeError]: setLoaded,
    [UpdateHealthNotice]: setLoading,
    [UpdateHealthNoticeSuccess]: setLoaded,
    [UpdateHealthNoticeError]: setLoaded,
    [PublishHealthNotice]: setLoading,
    [PublishHealthNoticeSuccess]: setLoaded,
    [PublishHealthNoticeError]: setLoaded,
    [CancelHealthNotice]: setLoading,
    [CancelHealthNoticeSuccess]: setLoaded,
    [CancelHealthNoticeError]: setLoaded,
    [DeleteHealthNotice]: setLoading,
    [DeleteHealthNoticeSuccess]: setLoaded,
    [DeleteHealthNoticeError]: setLoaded,

    // Image Library
    [GetImages]: setLoading,
    [GetImagesSuccess]: onGetImagesSuccess,
    [GetImagesError]: setLoaded,
    [UploadImage]: setLoading,
    [UploadImageSuccess]: setLoaded,
    [UploadImageError]: setLoaded,
    // Allergies
    [GetAllergies]: setLoading,
    [GetAllergiesSuccess]: onGetAllergiesSuccess,
    [GetAllergiesError]: onError,
    [AddAllergy]: setLoading,
    [AddAllergySuccess]: setLoaded,
    [AddAllergyError]: onError,
    [UpdateAllergy]: setLoading,
    [UpdateAllergySuccess]: setLoaded,
    [UpdateAllergyError]: onError,
    [SetAllergyModal]: onSetAllergyModal,

    // Conditions
    [GetConditions]: setLoading,
    [GetConditionsSuccess]: onGetConditionsSuccess,
    [GetConditionsError]: onError,
    [AddCondition]: setLoading,
    [AddConditionSuccess]: setLoaded,
    [AddConditionError]: onError,
    [UpdateCondition]: setLoading,
    [UpdateConditionSuccess]: setLoaded,
    [UpdateConditionError]: onError,
    [SetConditionModal]: onSetConditionModal,

    // Medications
    [GetMedications]: setLoading,
    [GetMedicationsSuccess]: onGetMedicationsSuccess,
    [GetMedicationsError]: onError,
    [GetMedicationTypes]: setLoading,
    [GetMedicationTypesSuccess]: onGetMedicationTypesSuccess,
    [GetMedicationTypesError]: onError,
    [AddMedication]: setLoading,
    [AddMedicationSuccess]: setLoaded,
    [AddMedicationError]: onError,
    [UpdateMedication]: setLoading,
    [UpdateMedicationSuccess]: setLoaded,
    [UpdateMedicationError]: onError,
    [SetMedicationModal]: onSetMedicationModal,

    // Vaccinations
    [GetVaccinations]: setLoading,
    [GetVaccinationsSuccess]: onGetVaccinationsSuccess,
    [GetVaccinationsError]: onError,
    [AddVaccination]: setLoading,
    [AddVaccinationSuccess]: setLoaded,
    [AddVaccinationError]: onError,
    [UpdateVaccination]: setLoading,
    [UpdateVaccinationSuccess]: setLoaded,
    [UpdateVaccinationError]: onError,
    [SetVaccinationModal]: onSetVaccinationModal,

    // Providers
    [GetProviders]: setLoading,
    [GetProvidersSuccess]: onGetProvidersSuccess,
    [GetProvidersError]: onError,
    [AddProvider]: setLoading,
    [AddProviderSuccess]: setLoaded,
    [AddProviderError]: onError,
    [UpdateProvider]: setLoading,
    [UpdateProviderSuccess]: setLoaded,
    [UpdateProviderError]: onError,
    [SetProviderModal]: onSetProviderModal,
    [GetProviderLogo]: setLoading,
    [GetProviderLogoSuccess]: onGetProviderLogoSuccess,
    [GetProviderLogoError]: onError,
    // Service Types
    [GetServiceTypes]: setLoading,
    [GetServiceTypesSuccess]: onGetServiceTypesSuccess,
    [GetServiceTypesError]: onError,
    [AddServiceType]: setLoading,
    [AddServiceTypeSuccess]: setLoaded,
    [AddServiceTypeError]: onError,
    [UpdateServiceType]: setLoading,
    [UpdateServiceTypeSuccess]: setLoaded,
    [UpdateServiceTypeError]: onError,
    [SetServiceTypeModal]: onSetServiceTypeModal,
    [GetWellbeingCategories]: setLoading,
    [GetWellbeingCategoriesSuccess]: onGetWellbeingCategoriesSuccess,
    [GetWellbeingCategoriesError]: onError,
    [GetParentAbsenceReasons]: setLoading,
    [GetParentAbsenceReasonsSuccess]: onGetParentAbsenceReasonsSuccess,
    [GetParentAbsenceReasonsError]: onError,

    // Care Plan Activities
    [GetCarePlanActivities]: setLoading,
    [GetCarePlanActivitiesSuccess]: onGetCarePlanActivitiesSuccess,
    [GetCarePlanActivitiesError]: onError,
    [GetCarePlanActivityTypes]: setLoading,
    [GetCarePlanActivityTypesSuccess]: onGetCarePlanActivityTypesSuccess,
    [GetCarePlanActivityTypesError]: onError,
    [GetCarePlanActivityPeriods]: setLoading,
    [GetCarePlanActivityPeriodsSuccess]: onGetCarePlanActivityPeriodsSuccess,
    [GetCarePlanActivityPeriodsError]: onError,
    [GetCarePlanActivityParents]: setLoading,
    [GetCarePlanActivityParentsSuccess]: onGetCarePlanActivityParentsSuccess,
    [GetCarePlanActivityParentsError]: onError,
    [GetCarePlanActivityCounters]: setLoading,
    [GetCarePlanActivityCountersSuccess]: onGetCarePlanActivityCountersSuccess,
    [GetCarePlanActivityCountersError]: onError,
    [AddCarePlanActivity]: setLoading,
    [AddCarePlanActivitySuccess]: setLoaded,
    [AddCarePlanActivityError]: onError,
    [UpdateCarePlanActivity]: setLoading,
    [UpdateCarePlanActivitySuccess]: setLoaded,
    [UpdateCarePlanActivityError]: onError,
    [SetCarePlanActivityModal]: onSetCarePlanActivityModal,
    [GetSupportPlanActivities]: setLoading,
    [GetSupportPlanActivitiesSuccess]: onGetSupportPlanActivitiesSuccess,
    [GetSupportPlanActivitiesError]: onError,
    [GetSupportPlanActivityCounters]: setLoading,
    [GetSupportPlanActivityCountersSuccess]: onGetSupportPlanActivityCountersSuccess,
    [GetSupportPlanActivityCountersError]: onError,
    [AddSupportPlanActivity]: setLoading,
    [AddSupportPlanActivitySuccess]: setLoaded,
    [AddSupportPlanActivityError]: onError,
    [UpdateSupportPlanActivity]: setLoading,
    [UpdateSupportPlanActivitySuccess]: setLoaded,
    [UpdateSupportPlanActivityError]: onError,
    [SetSupportPlanActivityModal]: onSetSupportPlanActivityModal,
    [GetSupportPlanJobDutyConfigs]: setLoading,
    [GetSupportPlanJobDutyConfigsSuccess]: onGetSupportPlanJobDutyConfigsSuccess,
    [GetSupportPlanJobDutyConfigsError]: onError,
    [GetTriageQuestions]: setLoading,
    [GetTriageQuestionsSuccess]: onGetTriageQuestionsSuccess,
    [GetTriageQuestionsError]: onError,
    [GetTriageQuestionSymptoms]: setLoading,
    [GetTriageQuestionSymptomsSuccess]: onGetTriageQuestionSymptomsSuccess,
    [GetTriageQuestionSymptomsError]: onError,
    [AddTriageQuestion]: setLoading,
    [AddTriageQuestionSuccess]: setLoaded,
    [AddTriageQuestionError]: onError,
    [UpdateTriageQuestion]: setLoading,
    [UpdateTriageQuestionSuccess]: setLoaded,
    [UpdateTriageQuestionError]: onError,
    [SetTriageQuestionModal]: onSetTriageQuestionModal,
    [GetTriageAdvices]: setLoading,
    [GetTriageAdvicesSuccess]: onGetTriageAdvicesSuccess,
    [GetTriageAdvicesError]: onError,
    [GetTriageAdviceSymptoms]: setLoading,
    [GetTriageAdviceSymptomsSuccess]: onGetTriageAdviceSymptomsSuccess,
    [GetTriageAdviceSymptomsError]: onError,
    [AddTriageAdvice]: setLoading,
    [AddTriageAdviceSuccess]: setLoaded,
    [AddTriageAdviceError]: onError,
    [UpdateTriageAdvice]: setLoading,
    [UpdateTriageAdviceSuccess]: setLoaded,
    [UpdateTriageAdviceError]: onError,
    [SetTriageAdviceModal]: onSetTriageAdviceModal,
  };
  if (action && action.type && act && act[action.type]) {
    return act[action.type](cloneObject(state))(action);
  }
  return state;
};
